import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import  moment from 'moment';
import { StreamState } from 'src/app/models/stream';

@Injectable({
  providedIn: 'root'
})
export class SoundPlayerService {
  private stop$ = new Subject();
  public audioObj = new Audio();
  audioEvents = [
    'ended', 
    'error', 
    'play', 
    'playing', 
    'pause', 
    'timeupdate',
    'loop',
    'canplay', 
    'loadedmetadata', 
    'loadstart'
  ];

  public state: StreamState;
  private stateChange: BehaviorSubject<any> = new BehaviorSubject({});

  constructor() {
    this.initializeState();
  }

  initializeState() {
    this.state = {
      playing: false,
      readableCurrentTime: '00:00',
      readableDuration: '00:00',
      duration: undefined,
      currentTime: undefined,
      ended: false,
      volume:1,
      canplay: false,
      error: false,
      currentIndex: 0,
    };
    this.stateChange.next(this.state);
  }

  streamObservable(url: any) {
    return new Observable(observer => {
      let hls: any = null;
      let destroyed = false;

      const handler = (event: Event) => {
        this.updateStateEvents(event);
        observer.next(event);
      };

      const startPlayback = async () => {
        try {
          const { default: Hls } = await import('hls.js');

          if (destroyed) {
            return;
          }

          if (!Hls.isSupported()) {
            this.audioObj.src = url;
            await this.audioObj.play();
          } else {
            hls = new Hls({
              autoStartLoad: true,
              debug: true,
              enableWorker: true
            });

            hls.attachMedia(this.audioObj);
            hls.loadSource(url);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              this.audioObj.play();
            });
          }

          this.addEvents(this.audioObj, this.audioEvents, handler);

        } catch (error) {
          console.error('Failed to load HLS player:', error);
          this.state.error = true;
          this.stateChange.next(this.state);
          observer.error(error);
        }
      };

      startPlayback();

      return () => {
        destroyed = true;

        this.audioObj.pause();
        this.audioObj.currentTime = 0;

        this.removeEvents(this.audioObj, this.audioEvents, handler);
        this.resetState();

        if (hls) {
          hls.destroy();
          hls = null;
        }
      };
    });
  }

  private addEvents(obj:any, events:any, handler:any) {
    events.forEach((event:any) => {
      obj.addEventListener(event, handler);
    });
  }

  private removeEvents(obj:any, events:any, handler:any) {
    events.forEach((event:any) => {
      obj.removeEventListener(event, handler);
    });
  }
  

  playStream(url:any) {
    this.audioObj = new Audio();
    return this.streamObservable(url).pipe(takeUntil(this.stop$));
  }

  play() {
    this.audioObj.play();
  }

  pause() {
    this.audioObj.pause();
  }

  loop(isLoop:boolean) {
    this.audioObj.loop = isLoop;
  }

  playRandom() {
    // this.audioObj.
  }

  stop() {
    this.stop$.next('');
  }

  seekTo(seconds:any) {
    this.audioObj.currentTime = seconds;
  }

  volumeChange(value:any) {
    this.audioObj.volume = value;
  }

  volumeMute(isMute:boolean) {
    this.audioObj.muted = isMute;
  }

  formatTime(time: number, format: string = 'mm:ss') {
    const momentTime = time * 1000;
    return moment.utc(momentTime).format(format);
  }


  updateStateEvents(event: Event): void {
    switch (event.type) {
      case 'canplay':
        this.state.duration = this.audioObj.duration;
        this.state.readableDuration = this.formatTime(this.state.duration);
        this.state.canplay = true;
        break;
      case 'playing':
        this.state.playing = true;
        break;
      case 'pause':
        this.state.playing = false;
        break;
      case 'ended' :
        this.state.ended = true; 
      break
      case 'timeupdate':
        this.state.currentTime = this.audioObj.currentTime;
        this.state.readableCurrentTime = this.formatTime(this.state.currentTime);
        break;
      case 'error':
        this.resetState();
        this.state.error = true;
        break;
    }
    this.stateChange.next(this.state);
  }

  resetState() {
    this.state = {
      playing: false,
      readableCurrentTime: '00:00',
      readableDuration: '00:00',
      duration: 0,
      currentTime: 0,
      volume:100,
      ended: false,
      canplay: false,
      error: false
    };
    this.stateChange.next(this.state);
  }

  resetStateData() {
   return {
      playing: false,
      readableCurrentTime: '00:00',
      readableDuration: '00:00',
      duration: 0,
      currentTime: 0,
      volume:100,
      ended: false,
      canplay: false,
      error: false
    }
  }

  getState(): Observable<StreamState> {
    return this.stateChange.asObservable();
  }

  isAudioPlayerIsMute() : boolean {
    return this.audioObj.muted;
  }

  isAudioPlayerIsLoop() : boolean {
    return this.audioObj.loop;
  }

  getVolumeRange()  {
    return  this.audioObj.volume;
  }

  isCurrentPlayChangeState() {
    if (this.state?.playing) {
      this.pause();
    } else {
      this.play();
    }
  }

  // playMusic(beatList:Array<any>,index:number) {
  //   beatList.filter( (item, arrIndex) => {
  //     if (arrIndex === index) {
  //       if (item.isPlaying) {
  //         this.isCurrentPlayChangeState();
  //       } else {
  //         this.crrentPlayMusic.next(item);
  //       }
  //     } else {
  //       item.isPlaying = false;
  //     }
  //   });
  //   return beatList;
  // }

}
